import type { FC } from 'react';
import { Link, useParams } from 'react-router-dom';
import { VStack } from '@chakra-ui/react';

import {
  CreditCard,
  FeatureFilterField,
  FeatureName,
  UserFilterValues,
} from '__generated__/GQL';
import useCanCompanyUserAccessFeature from 'common/hooks/useCanCompanyUserAccessFeature';
import Button from 'components/_core/Button';
import ErrorView from 'components/ErrorView';
import Loader from 'components/Loader';
import { FiltersList } from 'features/PaymentCards/pages/PaymentCardList/components/FiltersList';
import { usePaymentCardsListState } from 'features/PaymentCards/pages/PaymentCardList/components/PaymentCardsListFiltersProvider';
import {
  isCreditCardFragment,
  usePaymentCardsList,
} from 'features/PaymentCards/pages/PaymentCardList/hooks/usePaymentCardsList';
import * as routes from 'features/PaymentCards/routes';
import * as Layout from 'layouts/ContentLayout';

import EmptyList from './components/EmptyList';
import { PaymentCardListTable } from './components/PaymentCardListTable';
import { PaymentCardListViewPicker } from './components/PaymentCardListViewPicker';
import PaymentCardSidebar from './components/PaymentCardSidebar';
import { LAYOUT_BODY_CLASSNAME } from './constants';
import locales from './locales';

export const PaymentCardList: FC = () => {
  const { canAccessFeature } = useCanCompanyUserAccessFeature();
  const { canViewCompanyCards, dispatch } = usePaymentCardsListState();
  const { paymentCardId } = useParams<{ paymentCardId?: string }>();

  const {
    called,
    error,
    isFetchingMore,
    isLoading,
    loadMore,
    observable: paymentCardListObservable,
    pageInfo,
    paymentCards,
    previousDataPaymentCards,
  } = usePaymentCardsList();

  const canRequestPaymentCards = canAccessFeature({
    name: FeatureName.RequestPaymentcards,
    where: {
      or: [{ field: FeatureFilterField.User, values: [UserFilterValues.Self] }],
    },
  });
  const canOrderPaymentCards = canAccessFeature({
    name: FeatureName.PaymentcardsOrder,
  });

  const shouldDisplayAction = canOrderPaymentCards || canRequestPaymentCards;

  const currentData = paymentCards ?? previousDataPaymentCards;

  const isEmptyList = called && currentData?.length === 0;

  const selectedPaymentCard = currentData?.find(
    (card) => isCreditCardFragment(card) && card.creditCardId === paymentCardId,
  ) as CreditCard | undefined;

  if (error) {
    return <ErrorView />;
  }

  return (
    <Layout.Root>
      <Layout.Body className={LAYOUT_BODY_CLASSNAME} size="md">
        <Layout.Header
          action={
            shouldDisplayAction ? (
              <Button
                as={Link}
                icon="plus"
                iconPosition="left"
                to={canOrderPaymentCards ? routes.orderNew : routes.requestNew}
                variant="secondary"
              >
                {canOrderPaymentCards
                  ? locales.orderNewCard
                  : locales.requestNewCard}
              </Button>
            ) : null
          }
          title={locales.title}
        />
        <VStack alignItems="stretch" gap="space-24">
          {canViewCompanyCards ? <PaymentCardListViewPicker /> : null}
          <FiltersList />
          {isLoading && !currentData ? (
            <Loader />
          ) : (
            <>
              {isEmptyList ? (
                <EmptyList
                  onSearchReset={() => {
                    dispatch({
                      type: 'RESET_FILTERS',
                    });
                  }}
                />
              ) : (
                <PaymentCardListTable
                  isFetchingMore={isFetchingMore}
                  items={currentData ?? []}
                  listObservable={paymentCardListObservable}
                  onBottomReached={() => {
                    if (pageInfo.hasNextPage) {
                      loadMore();
                    }
                  }}
                  selectedItemId={paymentCardId ?? null}
                />
              )}
            </>
          )}
        </VStack>
      </Layout.Body>

      {selectedPaymentCard ? (
        <Layout.Sidebar
          borderLeft="1px solid"
          borderLeftColor="border"
          isScrollable
        >
          <PaymentCardSidebar paymentCard={selectedPaymentCard} />
        </Layout.Sidebar>
      ) : null}
    </Layout.Root>
  );
};
